import {
    Category as CustomIcon,
    Delete,
    Draw,
    LinearScale as RiverIcon,
    Route as RouteIcon,
    DirectionsWalk as TrailIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SvgIconComponent } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { MapEntityPath, MapPathStyle } from '../canvas/types';
import type { MapPathType, MapRoadQuality } from '../hooks/use-map-paths';
import { MAP_PATH_TYPES, MAP_ROAD_QUALITIES } from '../hooks/use-map-paths';
import { PathStyleEditor } from './PathStyleEditor';

const PATH_TYPE_ICONS: Record<MapPathType, SvgIconComponent> = {
    road: TrailIcon,
    river: RiverIcon,
    route: RouteIcon,
    custom: CustomIcon,
};

interface PathsPanelProps {
    paths: MapEntityPath[];
    isDrawingPath: boolean;
    selectedPathUid: string | null;
    onSelectPath: (uid: string | null) => void;
    onStartDrawing: (pathType: MapPathType, roadQuality?: MapRoadQuality) => void;
    onStopDrawing: () => void;
    onDeletePath: (uid: string) => void;
    onUpdateStyle: (uid: string, style: Partial<MapPathStyle>) => void;
    onUpdateMeta: (uid: string, meta: { name?: string; description?: string }) => void;
    onUpdateType: (uid: string, pathType: MapPathType, roadQuality?: MapRoadQuality) => void;
}

export function PathsPanel({
    paths,
    isDrawingPath,
    selectedPathUid,
    onSelectPath,
    onStartDrawing,
    onStopDrawing,
    onDeletePath,
    onUpdateStyle,
    onUpdateMeta,
    onUpdateType,
}: PathsPanelProps) {
    const { t } = useTranslation();

    const selectedPath = paths.find(p => p.uid === selectedPathUid) ?? null;

    return (
        <Stack spacing={1.5}>
            {/* Draw controls */}
            <NewPathControls
                isDrawingPath={isDrawingPath}
                onStartDrawing={onStartDrawing}
                onStopDrawing={onStopDrawing}
            />

            {isDrawingPath && (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'warning.dark', color: 'warning.contrastText' }}>
                    <Typography variant="caption" fontWeight="bold">
                        {t('map_creator.paths.draw_instructions')}
                    </Typography>
                </Box>
            )}

            <Divider />

            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                {t('map_creator.paths.placed_title', { count: paths.length })}
            </Typography>

            {paths.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    {t('map_creator.paths.empty')}
                </Typography>
            )}

            <List dense disablePadding>
                {paths.map(path => {
                    const isSelected = selectedPathUid === path.uid;
                    const Icon = PATH_TYPE_ICONS[path.pathType];
                    const label = path.name || t(`map_creator.paths.types.${path.pathType}`);
                    return (
                        <ListItem
                            key={path.uid}
                            disablePadding
                            sx={{
                                mb: 0.5, borderRadius: 1, border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch', p: 1,
                            }}
                            onClick={() => onSelectPath(isSelected ? null : path.uid)}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ width: 12, height: 3, borderRadius: 1, bgcolor: path.style.strokeColor, flexShrink: 0, mr: 0.5 }} />
                                <Icon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                                <ListItemText
                                    primary={label}
                                    secondary={t('map_creator.paths.vertices_count', { count: path.vertices.length })}
                                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.75rem', fontWeight: 'bold' }}
                                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
                                    sx={{ flex: 1, m: 0 }}
                                />
                                <Tooltip title={t('map_creator.paths.delete')}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={e => { e.stopPropagation(); onDeletePath(path.uid); }}
                                    >
                                        <Delete sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            {isSelected && selectedPath && (
                                <PathDetail
                                    path={selectedPath}
                                    onUpdateStyle={style => onUpdateStyle(path.uid, style)}
                                    onUpdateMeta={meta => onUpdateMeta(path.uid, meta)}
                                    onUpdateType={(pt, rq) => onUpdateType(path.uid, pt, rq)}
                                />
                            )}
                        </ListItem>
                    );
                })}
            </List>
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function NewPathControls({
    isDrawingPath, onStartDrawing, onStopDrawing,
}: { isDrawingPath: boolean; onStartDrawing: (t: MapPathType, rq?: MapRoadQuality) => void; onStopDrawing: () => void }) {
    const { t } = useTranslation();
    return (
        <Button
            variant={isDrawingPath ? 'contained' : 'outlined'}
            color={isDrawingPath ? 'warning' : 'primary'}
            startIcon={<Draw />}
            fullWidth
            size="small"
            onClick={() => isDrawingPath ? onStopDrawing() : onStartDrawing('road', 'dirt')}
        >
            {isDrawingPath
                ? t('map_creator.paths.drawing')
                : t('map_creator.paths.draw_button')}
        </Button>
    );
}

function PathDetail({
    path, onUpdateStyle, onUpdateMeta, onUpdateType,
}: {
    path: MapEntityPath;
    onUpdateStyle: (s: Partial<MapPathStyle>) => void;
    onUpdateMeta: (m: { name?: string; description?: string }) => void;
    onUpdateType: (pt: MapPathType, rq?: MapRoadQuality) => void;
}) {
    const { t } = useTranslation();

    return (
        <Stack spacing={1} mt={1} onClick={e => e.stopPropagation()}>
            <TextField
                size="small"
                label={t('map_creator.paths.name_label')}
                value={path.name ?? ''}
                onChange={e => onUpdateMeta({ name: e.target.value || undefined })}
                inputProps={{ style: { fontSize: '0.75rem' } }}
                InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                fullWidth
                onClick={e => e.stopPropagation()}
            />

            {/* Path type */}
            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.paths.type_label')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                    {MAP_PATH_TYPES.map(pt => (
                        <Chip
                            key={pt}
                            label={t(`map_creator.paths.types.${pt}`)}
                            size="small"
                            variant={path.pathType === pt ? 'filled' : 'outlined'}
                            onClick={e => { e.stopPropagation(); onUpdateType(pt, path.roadQuality); }}
                            sx={{ fontSize: '0.65rem' }}
                        />
                    ))}
                </Stack>
            </Box>

            {/* Road quality — only visible for road type */}
            {path.pathType === 'road' && (
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        {t('map_creator.paths.road_quality_label')}
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                        {MAP_ROAD_QUALITIES.map(rq => (
                            <Chip
                                key={rq}
                                label={t(`map_creator.paths.road_quality.${rq}`)}
                                size="small"
                                variant={path.roadQuality === rq ? 'filled' : 'outlined'}
                                color={path.roadQuality === rq ? 'secondary' : 'default'}
                                onClick={e => { e.stopPropagation(); onUpdateType('road', rq); }}
                                sx={{ fontSize: '0.65rem' }}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            <PathStyleEditor style={path.style} onChange={onUpdateStyle} />
        </Stack>
    );
}
